import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';
import { Modal } from './ui';
import { Avatar } from './People';
import type { ConversationView } from '@goyone/shared';

/**
 * قائمةُ خيارِ الرسالة — بطابعِ واتساب.
 *
 * كان الفعلُ نصًّا يظهر تحت الرسالةِ بخطٍّ عند المرور: «حذف» لا يكاد يُرى،
 * ويُسطَّر فيوهم أنه رابط. صار زرًّا صغيرًا في زاويةِ الفقاعةِ يفتح قائمةً
 * بأيقوناتٍ وأسماء — ردٌّ ونسخٌ وتحويلٌ وتعديلٌ وحذف — كلٌّ بما يخصّه.
 *
 * والقائمةُ تُغلق بنقرةٍ خارجها أو بمفتاح Esc، فلا تبقى معلّقةً فوق الحديث.
 */

export interface MsgAction {
  key: string;
  label: string;
  icon: IconName;
  danger?: boolean;
  onClick: () => void;
}

export function MessageMenu({
  actions,
  align = 'end',
}: {
  actions: MsgAction[];
  /** جهةُ انفتاحِ القائمة أفقيًّا — «end» نحو داخلِ الفقاعة. */
  align?: 'start' | 'end';
}) {
  const [open, setOpen] = useState(false);
  /*
   * القائمةُ تُرسم في طبقةٍ عائمةٍ على الصفحةِ (بوّابة) بموضعٍ ثابتٍ محسوبٍ
   * من موضعِ الزرِّ — لا داخلَ مجرى الرسائل. فمهما كانت الرسالةُ في أعلى
   * المجرى أو أسفلِه، لا يقصّها إطارُ المجرى: تظهر كاملةً، وتنقلبُ لأعلى أو
   * لأسفلَ حسبَ المتّسعِ في الشاشة.
   */
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (trigger.current?.contains(t) || pop.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    /* التمريرُ أو تغييرُ المقاسِ يفصلُ القائمةَ عن زرِّها — فتُغلق. */
    const onMove = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  if (actions.length === 0) return null;

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const el = trigger.current;
    if (!el) {
      setOpen(true);
      return;
    }
    const r = el.getBoundingClientRect();
    const estH = actions.length * 40 + 14;
    const estW = 180;
    const gap = 8;
    const below = window.innerHeight - r.bottom;
    /* عموديًّا: ينفتحُ لأعلى حين يضيقُ ما تحتَه ويتّسعُ ما فوقه، وإلا فلأسفل. */
    const up = below < estH && r.top > below;

    const style: CSSProperties = { position: 'fixed', minWidth: 152 };
    if (up) style.bottom = Math.max(gap, window.innerHeight - r.top + 4);
    else style.top = Math.min(window.innerHeight - estH - gap, r.bottom + 4);

    /*
     * أفقيًّا: تبدأُ من محاذاةِ حافّةِ الزرِّ، ثم تُضبَط داخلَ الشاشة.
     *
     * الزرُّ قد يكونُ عند حافّةٍ (طرفِ سطرٍ في ملءِ الشاشة)، فمحاذاةُ الحافّةِ
     * وحدَها تُخرج القائمةَ عن النطاق — فتُقصَر بين هامشين لتظهرَ كاملة.
     */
    const raw = align === 'end' ? r.right - estW : r.left;
    style.left = Math.min(Math.max(gap, raw), window.innerWidth - estW - gap);
    setPos(style);
    setOpen(true);
  };

  return (
    <div className={`msg-menu${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="msg-menu-trigger"
        ref={trigger}
        onClick={toggle}
        title="خيارات الرسالة"
        aria-label="خيارات الرسالة"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="chevronDown" size={15} />
      </button>

      {open &&
        pos &&
        createPortal(
          <div className="msg-menu-pop" role="menu" ref={pop} style={pos}>
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                className={`msg-menu-item${a.danger ? ' is-danger' : ''}`}
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
              >
                <Icon name={a.icon} size={15} />
                <span>{a.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

/**
 * منتقي وجهةِ التحويل.
 *
 * تحويلُ رسالةٍ يُرسل نصَّها إلى محادثةٍ أخرى — فتُعرض المحادثاتُ ليُنتقى
 * منها، ويُرسَل النصُّ إلى المختارةِ بمسارِ الإرسالِ نفسِه.
 */
export function ForwardPicker({
  conversations,
  onPick,
  onClose,
  busy,
}: {
  conversations: ConversationView[];
  onPick: (conversationId: string) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const [q, setQ] = useState('');
  const mine = conversations
    .filter((c) => c.joined)
    .filter((c) => !q.trim() || c.title.includes(q.trim()));

  return (
    <Modal title="تحويل الرسالة" onClose={onClose}>
      <div className="dock-search" style={{ marginBottom: 'var(--sp-3)' }}>
        <Icon name="search" size={15} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث عن محادثة"
          aria-label="ابحث عن محادثة"
          autoFocus
        />
      </div>

      {mine.length === 0 && <p className="mute center">لا محادثة تطابق.</p>}

      <div className="pick-list">
        {mine.map((c) => (
          <button
            key={c.id}
            className="pick-row"
            onClick={() => onPick(c.id)}
            disabled={busy}
          >
            {c.kind === 'DIRECT' ? (
              <Avatar name={c.title} size={30} />
            ) : (
              <span className="dock-group-avatar" style={{ width: 30, height: 30 }} aria-hidden="true">
                {c.isPrivate ? <Icon name="users" size={14} /> : '#'}
              </span>
            )}
            <span className="pick-row-text">
              <strong>{c.title}</strong>
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

/** نسخُ نصِّ رسالةٍ إلى الحافظة — يعملُ حيث تتاح الحافظةُ، ويصمت حيث تُمنع. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * اقتباسُ رسالةٍ للردِّ عليها — يُوضع في حقلِ الكتابةِ فوقَ ردِّ المستخدم.
 *
 * بلا مقبسٍ للخيوط في الخادم، الاقتباسُ نصٌّ مكتفٍ بذاته: يُحفظ في متنِ
 * الرسالةِ فيبقى بعد التحديث، ويُقرأ «ردٌّ على فلان: …» فوق الجواب.
 */
export function buildReplyQuote(name: string | null | undefined, body: string): string {
  const who = name ? `${name.split(' ')[0]}: ` : '';
  const flat = body.replace(/\s+/g, ' ').trim();
  const snip = flat.length > 90 ? `${flat.slice(0, 90)}…` : flat;
  return `❝ ${who}${snip} ❞\n`;
}
